import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

import { Injectable } from '@angular/core';
import { collectionData, doc, docData, DocumentReference, orderBy, query, Timestamp, where } from '@angular/fire/firestore';
import { IInventoryLocation, IInventoryStockAdjustment, IInventoryStockAdjustmentWithData } from '@inventory/models/inventory';
import { FireStoreCollection } from '@shared/models/firestore';
import { FirestoreService } from '@shared/services/firestore/firestore.service';

@Injectable({
	providedIn: 'root',
})
export class InventoryStockAdjustmentService extends FirestoreService<IInventoryStockAdjustment> {
	constructor() {
		super(FireStoreCollection.INVENTORY_STOCK_ADJUSTMENT);
	}

	getInventoryStockAdjustmentsByItem(inventoryItemId: string): Observable<Array<IInventoryStockAdjustment>> {
		const inventoryItemRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_ITEMS}/${inventoryItemId}`);
		let inventoryQuery = query(this.collectionRef, where('inventoryItem', '==', inventoryItemRef));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryStockAdjustment>(inventoryQuery, { idField: 'id' });
	}

	getInventoryStockAdjustmentsByItemWithData(inventoryItemId: string): Observable<Array<IInventoryStockAdjustmentWithData>> {
		const inventoryItemRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_ITEMS}/${inventoryItemId}`);
		const adjustmentsQuery = query(this.collectionRef, where('inventoryItem', '==', inventoryItemRef), orderBy('createdDate', 'desc'));

		return collectionData<IInventoryStockAdjustment>(adjustmentsQuery, { idField: 'id' }).pipe(
			switchMap((items) => {
				const locationRefs = Array.from(
					new Set(items.map((i) => i.inventoryLocation).filter((ref): ref is DocumentReference => !!ref)),
				);

				if (locationRefs.length === 0) {
					return of(items.map((i) => ({ ...i, locationData: null })));
				}

				const locationObservables = locationRefs.map((ref) =>
					docData<IInventoryLocation>(ref as DocumentReference<IInventoryLocation>, { idField: 'id' }),
				);

				return combineLatest(locationObservables).pipe(
					map((locations) => {
						const locationMap = new Map(locationRefs.map((ref, i) => [ref.path, locations[i]]));
						return items.map((item) => ({
							...item,
							locationData: item.inventoryLocation ? locationMap.get(item.inventoryLocation.path) || null : null,
						}));
					}),
				);
			}),
		);
	}

	async createInventoryStockAdjustment(
		organisationRef: DocumentReference,
		userId: string | undefined,
		inventoryItemRef: DocumentReference,
		inventoryLocationRef: DocumentReference,
		quantity: number,
		changeType: 'increase' | 'decrease',
		reason: string | null,
	): Promise<DocumentReference<IInventoryStockAdjustment>> {
		const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${userId}`);

		return this.create({
			organisation: organisationRef,
			user: userRef,
			inventoryItem: inventoryItemRef,
			inventoryLocation: inventoryLocationRef,
			quantity: Number(quantity),
			changeType: changeType,
			reason: reason,
			status: 'pending',
			createdDate: Timestamp.now(),
			updatedDate: Timestamp.now(),
		});
	}

	async createInventoryStockAdjustmentById(
		organisationRef: DocumentReference,
		userId: string | undefined,
		inventoryItemId: string,
		inventoryLocationId: string,
		quantity: number,
		changeType: 'increase' | 'decrease',
		reason: string | null,
	): Promise<DocumentReference<IInventoryStockAdjustment>> {
		const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${userId}`);
		const inventoryItemRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_ITEMS}/${inventoryItemId}`);
		const inventoryLocationRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_LOCATIONS}/${inventoryLocationId}`);

		return this.create({
			organisation: organisationRef,
			user: userRef,
			inventoryItem: inventoryItemRef,
			inventoryLocation: inventoryLocationRef,
			quantity: Number(quantity),
			changeType: changeType,
			reason: reason,
			status: 'pending',
			createdDate: Timestamp.now(),
			updatedDate: Timestamp.now(),
		});
	}
}
